import { isWebView } from 'shared/helpers/utils/url';
import { getDeviceType, DEVICE_TYPE } from 'shared/helpers/app';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';
import { detectOS, detectWebView } from 'server/helpers/deviceUtility';
import { BROWSER_VALUE } from 'server/helpers/deviceUtility/constants';

export const GRAHAK_AUTH_SURFACE = {
  DWEB: 'dweb',
  MWEB: 'mweb',
  WEBVIEW: 'webview',
} as const;

export type GrahakAuthSurface = (typeof GRAHAK_AUTH_SURFACE)[keyof typeof GRAHAK_AUTH_SURFACE];

const isDesktopSurface = () => getDeviceType() === DEVICE_TYPE.DESKTOP;

const isCurrentlyWebView = () =>
  typeof window !== 'undefined' &&
  (detectWebView(window.navigator.userAgent) !== BROWSER_VALUE ||
    isWebView(window.location as any));

export const getGrahakAuthSurfaceFromState = (state?: any): GrahakAuthSurface | null => {
  const grahakAuthConfig = state ? getRemoteConfig(state, 'grahakAuth') : undefined;

  const {
    onlyEnableForDweb = false,
    onlyEnableForMweb = false,
    enableForWebView = false,
    enableForAndroidWebView = true,
    enableForIosWebView = true,
  } = grahakAuthConfig || {};

  if (isCurrentlyWebView()) {
    if (!enableForWebView) return null;

    const userAgent = window.navigator.userAgent;
    const webView = detectWebView(userAgent);
    // Prefer app markers; URL-only webviews fall back to OS detection.
    let os =
      webView === 'Android WebView'
        ? 'android'
        : webView === 'iOS WebView'
        ? 'iOS'
        : detectOS(userAgent);
    // iPadOS desktop-mode UAs identify as Mac; multitouch distinguishes them.
    if (os === 'mac' && window.navigator.maxTouchPoints > 1) {
      os = 'iOS';
    }

    const enabledForOS =
      os === 'android'
        ? enableForAndroidWebView
        : os === 'iOS'
        ? enableForIosWebView
        : enableForAndroidWebView && enableForIosWebView;

    return enabledForOS ? GRAHAK_AUTH_SURFACE.WEBVIEW : null;
  }

  if (isDesktopSurface()) {
    return onlyEnableForDweb ? GRAHAK_AUTH_SURFACE.DWEB : null;
  }

  return onlyEnableForMweb ? GRAHAK_AUTH_SURFACE.MWEB : null;
};

export const isEligibleforGrahakRuleFromState = (state?: any) =>
  getGrahakAuthSurfaceFromState(state) !== null;
